import axios from '@/lib/axios'
import { useUsersCompanyStore } from '@/stores/usersCompany'
import { useQuery } from '@tanstack/vue-query'
import { get, useDebounceFn } from '@vueuse/core'
import { group } from 'console'
import _ from 'lodash'
import { storeToRefs } from 'pinia'
import { onBeforeMount, onUnmounted, ref, watch } from 'vue'

import { useAuth } from '@/composables/useAuth'

import type { PaginatedResponse } from '@/interfaces/paginatedResponse'

import type { User } from '../interfaces/user'
import { UserFilters } from '../interfaces/userFilters'

interface Props {
	immediate?: boolean
}

interface Params extends UserFilters {
	ids?: Array<number | string>
}

const getUsersCompany = async (
	params: Params,
	page?: string
): Promise<PaginatedResponse<User>> => {
	const { data } = await axios.get<PaginatedResponse<User>>(
		`api/companies/${get(params.company)}/users?page=${page}`,
		{
			params: {
				// Solo enviar search si tiene valor
				...(params.search &&
					params.search.trim() !== '' && { search: params.search }),
				// Solo enviar role si no es null/undefined y no es texto de "Todos"
				...(params.role !== null &&
					params.role !== undefined &&
					!params.role.toString().toLowerCase().includes('todos') && {
						role: params.role,
					}),
				// Solo enviar status si no es null/undefined y no es texto de "Todos"
				...(params.status !== null &&
					params.status !== undefined &&
					!params.status.toString().toLowerCase().includes('todos') && {
						status: params.status,
					}),
				// Solo enviar department si no es null/undefined y no es texto de "Todos"
				...(params.department !== null &&
					params.department !== undefined &&
					!params.department.toString().toLowerCase().includes('todos') && {
						department: params.department,
					}),
				// Solo enviar group si no es null/undefined y no es texto de "Todos"
				...(params.group !== null &&
					params.group !== undefined &&
					!params.group.toString().toLowerCase().includes('todos') && {
						group: params.group,
					}),
				// Solo enviar supervisor si tiene valor (id del supervisor asignado)
				...(params.supervisor !== null &&
					params.supervisor !== undefined && {
						supervisor: params.supervisor,
					}),
				// Solo enviar sortBy si no es null/undefined, usar 'name' como default
				...(params.sortBy !== null &&
					params.sortBy !== undefined && { sortBy: params.sortBy || 'name' }),
				// Solo enviar sortDirection si no es null/undefined y tiene valor
				...(params.sortDirection !== null &&
					params.sortDirection !== undefined && {
						sortDirection: params.sortDirection,
					}),
				...(params.perPage && { per_page: params.perPage }),
				// Filtros rápidos: sólo viajan cuando están encendidos. Apagados se
				// omiten en vez de mandar `false`, porque el backend los evalúa con
				// filter_var y un false explícito sólo ensuciaría el query string.
				...(params.withoutSupervisor && { withoutSupervisor: true }),
				...(params.withoutDepartment && { withoutDepartment: true }),
				...(params.withoutAccess && { withoutAccess: true }),
			},
		}
	)

	return data
}

const useUsersCompany = (params?: Params, props: Props = {}) => {
	const { immediate = true } = props

	const { setUsers, setPagination, setFiltersValue, setIncludes, setPage } =
		useUsersCompanyStore()
	const { user } = useAuth()
	const { users, page, pagination, filters } = storeToRefs(
		useUsersCompanyStore()
	)

	watch(
		() => user.value?.companyId,
		(newId) => {
			if (newId && !filters.value.company) {
				filters.value.company = newId
			}
		},
		{ immediate: true }
	)

	const { data, isError, isLoading, isFetching, refetch, isRefetching } =
		useQuery(
			['usersCompany'],
			() =>
				getUsersCompany(
					{
						company: params?.company,
						...filters.value,
						...(params ?? {}),
					},
					page.value
				),
			{
				retry: false,
				enabled: immediate,
			}
		)

	const searchUsers = async () => {
		await refetch()
	}

	watch(
		data,
		(response) => {
			if (response) {
				setUsers(response.data)
				setPagination(_.omit(response, ['data']))
			}
		},
		{ immediate: true }
	)

	const debouncedSearchUsers = useDebounceFn(searchUsers, 400)

	// Un solo watcher debounced para todos los filtros: evita que un reset
	// (p.ej. status/page al escribir en el buscador) dispare un segundo
	// fetch inmediato en paralelo al de la búsqueda de texto.
	watch(filters, debouncedSearchUsers, { deep: true })

	watch(
		page,
		() => {
			searchUsers()
		}
	)

	return {
		data,
		users,
		filters,
		pagination,
		page,
		isError,
		isFetching,
		isLoading,

		setFiltersValue,
		refetch,
		searchUsers,
		setIncludes,
		setPage,
		isRefetching,
	}
}

export default useUsersCompany
